import type { CSSProperties } from 'react'
import type { SettingsRow } from '@/types'

import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { burstConfetti } from '@/lib/celebrate'

import {
    Cake,
    ChevronDown,
    Heart,
    Sparkles
} from 'lucide-react'

import Countdown from '@/components/landing/countdown'
import FloatingHearts from '@/components/landing/floating-hearts'
import Photo from '@/components/landing/photo'

interface HeroSectionProps {
    settings: SettingsRow | null
    opened: boolean
    nextId?: string
}

export default function HeroSection({ settings, opened, nextId }: HeroSectionProps) {
    useEffect(() => {
        if (!opened) return

        const timer = window.setTimeout(() => {
            void burstConfetti()
        }, 300)

        return () => window.clearTimeout(timer)
    }, [opened])

    const enter = (delay: number): { className: string, style?: CSSProperties } => {
        if (!opened) return { className: 'opacity-0 motion-reduce:opacity-100' }

        return {
            className: 'animate-in fade-in slide-in-from-bottom-6 fill-mode-both duration-1000 ease-out motion-reduce:animate-none',
            style: { animationDelay: `${delay}ms` }
        }
    }

    const kicker = enter(100)
    const title = enter(250)
    const subtitle = enter(450)
    const countdown = enter(650)
    const photo = enter(400)

    return (
        <section id='hero' className='relative isolate flex min-h-svh items-center overflow-hidden bg-night text-cream'>
            <div aria-hidden className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,#7a2149_0%,#2e1028_50%,#1a0916_100%)]' />
            <div aria-hidden className='star-dust absolute inset-0 -z-10 opacity-70' />
            <div aria-hidden className='absolute -top-40 -right-40 -z-10 size-[28rem] rounded-full bg-petal/20 blur-3xl' />
            <div aria-hidden className='absolute -bottom-48 -left-32 -z-10 size-[30rem] rounded-full bg-gold/10 blur-3xl' />

            <FloatingHearts className='-z-10' />

            <div className='mx-auto grid w-full max-w-6xl items-center gap-14 px-6 pt-20 pb-32 md:grid-cols-[1.15fr_0.85fr] md:gap-10 lg:px-10'>
                <div className='flex flex-col items-center text-center md:items-start md:text-left'>
                    {settings?.hero_kicker && (
                        <p
                            className={cn('inline-flex items-center gap-2 rounded-full bg-white/[0.08] px-4 py-2 text-sm font-semibold text-[#ffd1dc] ring-1 ring-white/15', kicker.className)}
                            style={kicker.style}
                        >
                            <Sparkles aria-hidden className='size-4 text-gold' />

                            <span data-mdlfy-cms={`settings:${settings.id}:hero_kicker`}>
                                {settings.hero_kicker}
                            </span>
                        </p>
                    )}

                    <h1 className={cn('mt-6', title.className)} style={title.style}>
                        {settings?.hero_title && (
                            <span
                                data-mdlfy-cms={`settings:${settings.id}:hero_title`}
                                className='block font-script text-6xl leading-[0.95] text-[#ffd1dc] sm:text-7xl lg:text-8xl'
                            >
                                {settings.hero_title}
                            </span>
                        )}

                        {settings?.her_name && (
                            <span
                                data-mdlfy-cms={`settings:${settings.id}:her_name`}
                                className='mt-2 block bg-gradient-to-r from-[#ffe0e8] via-[#ff8fae] to-gold bg-clip-text pb-2 font-heading text-7xl leading-none font-bold tracking-tight text-transparent sm:text-8xl lg:text-9xl'
                            >
                                {settings.her_name}
                            </span>
                        )}
                    </h1>

                    {settings?.hero_subtitle && (
                        <p
                            data-mdlfy-cms={`settings:${settings.id}:hero_subtitle`}
                            className={cn('mt-6 max-w-lg text-lg leading-relaxed text-pretty text-cream/80 sm:text-xl', subtitle.className)}
                            style={subtitle.style}
                        >
                            {settings.hero_subtitle}
                        </p>
                    )}

                    {settings && (
                        <div className={cn('mt-9 flex w-full justify-center md:justify-start', countdown.className)} style={countdown.style}>
                            <Countdown settings={settings} />
                        </div>
                    )}
                </div>

                {settings?.hero_photo && (
                    <div className={cn('relative mx-auto w-[min(76vw,340px)] md:w-full md:max-w-[380px]', photo.className)} style={photo.style}>
                        <div aria-hidden className='absolute -inset-3 rounded-t-full rounded-b-[2.6rem] border border-[#ffd1dc]/30 sm:-inset-4' />
                        <div aria-hidden className='absolute -inset-7 rounded-t-full rounded-b-[3rem] border border-dashed border-[#ffd1dc]/15 sm:-inset-9' />

                        <Photo
                            src={settings.hero_photo}
                            alt={settings.hero_photo_alt ?? ''}
                            position='50% 30%'
                            eager
                            className='aspect-[4/5] rounded-t-full rounded-b-[2rem] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/20'
                        />

                        <div aria-hidden className='absolute -bottom-5 -left-4 grid size-16 animate-bob place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-xl ring-4 ring-night motion-reduce:animate-none sm:-left-6'>
                            <Heart className='size-7 fill-cream text-cream' />
                        </div>

                        <div aria-hidden className='absolute -top-2 -right-2 grid size-14 place-items-center rounded-full bg-cream text-petal-deep shadow-xl ring-4 ring-night [animation-delay:1.2s] animate-bob motion-reduce:animate-none sm:-right-5'>
                            <Cake className='size-6' />
                        </div>
                    </div>
                )}
            </div>

            {nextId && (
                <a
                    href={`#${nextId}`}
                    className={cn(
                        'absolute bottom-7 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold tracking-[0.2em] text-cream/70 uppercase transition-colors outline-none hover:text-cream focus-visible:ring-2 focus-visible:ring-[#ffd1dc]/60',
                        opened ? 'animate-in fade-in fill-mode-both delay-1000 duration-1000' : 'opacity-0'
                    )}
                >
                    <span>
                        Neeche chalo
                    </span>

                    <ChevronDown aria-hidden className='size-5 animate-bounce motion-reduce:animate-none' />
                </a>
            )}
        </section>
    )
}
